import React from "react";
import { SpotRequest, MergeSpotRequest, SpotAddRequest, getMergeRequestDistance, calculateDistance } from "@/common/model/SpotRequest";
import { isProtectedSpot } from "@/constants/spots";

interface RequestCardProps {
  request: SpotRequest;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: () => void;
  onHover: () => void;
  onHoverEnd: () => void;
  onReview?: () => void;
  onUserClick?: (userId: number, username: string) => void;
}

const RequestCard: React.FC<RequestCardProps> = ({
  request,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onHoverEnd,
  onReview,
  onUserClick,
}) => {
  const getTypeIcon = () => {
    switch (request.type) {
      case "merge":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      case "add":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case "rename":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        );
      case "edit":
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
    }
  };

  const getTypeColor = () => {
    switch (request.type) {
      case "merge":
        return "bg-purple-500/20 text-purple-400";
      case "add":
        return "bg-green-500/20 text-green-400";
      case "rename":
        return "bg-blue-500/20 text-blue-400";
      case "edit":
        return "bg-amber-500/20 text-amber-400";
    }
  };

  const getTypeLabel = () => {
    switch (request.type) {
      case "merge":
        return "MERGE";
      case "add":
        return "ADD";
      case "rename":
        return "RENAME";
      case "edit":
        return "EDIT";
    }
  };

  // Check if request involves a protected spot
  const hasProtectedSpot = (): boolean => {
    if (request.type === "merge") {
      const data = request.data as MergeSpotRequest;
      return isProtectedSpot(data.selectedspotid);
    }
    return false;
  };

  const renderMergeContent = () => {
    const data = request.data as MergeSpotRequest;
    const distance = getMergeRequestDistance(data);

    return (
      <>
        <div className="space-y-1">
          {/* Source spot (red) */}
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0" />
            <span className="text-gray-300 text-sm truncate">{data.sourcespotname}</span>
          </div>

          {/* Arrow */}
          <div className="flex items-center gap-2 pl-0.5">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span className="text-xs text-gray-500">{distance.toFixed(1)} km</span>
          </div>

          {/* Target spot (green) */}
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0" />
            <span className="text-gray-300 text-sm truncate">{data.targetspotname}</span>
          </div>
        </div>

        {/* Stats row */}
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="text-red-400">{data.sourcensessions ?? 0}</span> sess
          </span>
          <span>→</span>
          <span className="flex items-center gap-1">
            <span className="text-green-400">{data.targetnsessions ?? 0}</span> sess
          </span>
        </div>
      </>
    );
  };

  const renderAddContent = () => {
    const data = request.data as SpotAddRequest;

    return (
      <>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-gray-300 text-sm truncate">"{data.spotname}"</span>
          </div>
        </div>

        {/* Location */}
        {data.countryName && (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
            {data.alpha2 && (
              <img
                src={`https://flagcdn.com/w20/${data.alpha2.toLowerCase()}.png`}
                alt={data.alpha2}
                className="w-4 h-auto"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )}
            <span>{data.countryName}</span>
          </div>
        )}
      </>
    );
  };

  const renderRenameContent = () => {
    const data = request.data as SpotAddRequest;

    return (
      <>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm line-through truncate">"{data.oldname}"</span>
          </div>
          <div className="flex items-center gap-2 pl-1">
            <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-blue-400 text-sm truncate">"{data.newname}"</span>
          </div>
        </div>
      </>
    );
  };

  const renderEditContent = () => {
    const data = request.data as SpotAddRequest;
    const nameChanged = data.oldname != null && data.oldname !== data.newname;
    const locationChanged = data.oldlatitude != null && data.oldlongitude != null &&
      (Math.abs(data.latitude - data.oldlatitude) > 0.0001 || Math.abs(data.longitude - data.oldlongitude) > 0.0001);
    const distance = locationChanged
      ? calculateDistance(data.oldlatitude!, data.oldlongitude!, data.latitude, data.longitude)
      : 0;

    return (
      <>
        <div className="space-y-2">
          {/* Name change */}
          {nameChanged && (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-gray-400 text-sm line-through truncate">"{data.oldname}"</span>
              </div>
              <div className="flex items-center gap-2 pl-1">
                <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-amber-400 text-sm truncate">"{data.newname}"</span>
              </div>
            </div>
          )}

          {/* Location change */}
          {locationChanged && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <svg className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <span>Location moved <strong className="text-amber-400">{distance.toFixed(2)} km</strong></span>
            </div>
          )}

          {/* Name unchanged label */}
          {!nameChanged && (
            <div className="text-sm text-gray-300 truncate">"{data.newname || data.oldname}"</div>
          )}
        </div>
      </>
    );
  };

  const data = request.data;
  const username = request.type === "merge"
    ? (data as MergeSpotRequest).username
    : (data as SpotAddRequest).username;
  const userid = request.type === "merge"
    ? (data as MergeSpotRequest).userid
    : (data as SpotAddRequest).userid;
  const createdAt = request.type === "merge"
    ? (data as MergeSpotRequest).createdAt
    : (data as SpotAddRequest).createdAt;
  const comment = request.type === "merge"
    ? (data as MergeSpotRequest).comment
    : (data as SpotAddRequest).comment;

  const formatRelativeTime = (dateString: string | undefined): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUserClick && userid) {
      onUserClick(userid, username || "");
    }
  };

  return (
    <div
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      className={`
        p-3 rounded-lg cursor-pointer transition-all duration-200
        ${isSelected
          ? "bg-blue-600 ring-2 ring-blue-400"
          : isHovered
          ? "bg-gray-700"
          : "bg-gray-800 hover:bg-gray-700"
        }
      `}
    >
      {/* Header with type badge */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`
            flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium
            ${getTypeColor()}
          `}>
            {getTypeIcon()}
            {getTypeLabel()}
          </span>
          {hasProtectedSpot() && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-500/20 text-amber-400">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              PROTECTED
            </span>
          )}
          {createdAt && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {formatRelativeTime(createdAt)}
            </span>
          )}
        </div>
        {request.type === "merge" && (
          <span className="text-xs text-gray-500">
            {getMergeRequestDistance(request.data as MergeSpotRequest).toFixed(1)} km
          </span>
        )}
      </div>

      {/* Content based on type */}
      {request.type === "merge" && renderMergeContent()}
      {request.type === "add" && renderAddContent()}
      {request.type === "rename" && renderRenameContent()}
      {request.type === "edit" && renderEditContent()}

      {/* User comment */}
      {comment && (
        <div className="mt-2 px-2 py-1.5 bg-gray-700/50 rounded text-xs text-gray-300 italic">
          <span className="text-gray-500 not-italic">&ldquo;</span>
          {comment}
          <span className="text-gray-500 not-italic">&rdquo;</span>
        </div>
      )}

      {/* Footer with user info and actions */}
      <div className="mt-3 pt-2 border-t border-gray-700">
        {userid && (
          <button
            onClick={handleUserClick}
            className="flex items-center gap-2 text-xs text-gray-400 mb-2 hover:text-blue-400 transition-colors group"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className="group-hover:underline">{username || "View user"}</span>
            <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        )}

        {/* Review button */}
        {onReview && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReview();
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Review Request
          </button>
        )}
      </div>
    </div>
  );
};

export default RequestCard;
